import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex'
import { Component, Prop, Watch } from 'vue-property-decorator'
import * as types from '@board/store/types'
import * as accountTypes from '@account/store/types'
import { getProperty, getLangProperty, IEDate, uGetDateFormat } from '@appbase/util';
import { open, OpenType } from '@board/open-handler'; 

import { LoadingBar } from '@appbase/components/loading'
import { ids } from '@board/module';

import UserCardComponent from '@appbase/components/common/UserCard/UserCardComponent';

@Component({
    components: {
        LoadingBar
    },

    computed: mapGetters({
        isLoading: types.BOARD_LOADDING,
        listFields: types.FETCH_LISTFIELD,
        articleList: types.FETCH_ARTICLES,
        popupBaseUrl: types.POPUP_BASEURL,
        pageSet: types.FETCH_PAGESET,
        boardConfig: types.BOARD_CONFIG,
        user: accountTypes.USER,
        current: accountTypes.CURRENT
    }),

    methods: mapActions({
        fetchArticle: types.FETCH_ARTICLES,
        fetchPageSet: types.FETCH_PAGESET
    })
})

export default class BoardList extends UserCardComponent {
    @Prop({ type: Number }) currentPage!: number;    
    @Prop({ type: Boolean }) adminMode!: boolean;     
    @Prop({ type: Boolean }) previewMode!: boolean;

    fetchArticle!: (searchset: article.SearchSet) => Promise<article.PagedData<article.Article>>;
    fetchPageSet!: (pageSet: boardModule.state.PageSet) => void;    

    isLoading!: boolean;
    listFields!: board.ListField[];
    articleList!: Array<article.Article>;
    pageSet!: boardModule.state.PageSet;
    popupBaseUrl!: string;
    user!: account.User;
    current!: account.TCurrent;
    boardConfig!: board.BoardItem;

    itemsPerPage: number = 15;   
    currentField: number = 0;
    sortOrder: boolean = false;
    searchItems: number = 0;
    currentPreface: string | null = null;
    currentPageNum :number = 0;

    hasReadPermission: boolean = false;

    articleCurrentId: number = 0;

    @Watch('boardConfig')
    onBoardChanged(newVal: board.BoardItem) {      
        this.fetchPageSet(<boardModule.state.PageSet>{
            boardId: newVal.boardId,
            currentPage: 1,
            searchField: 0,
            searchText: null,
            preface1: null,
            preface2: null
        });
    }
  
    @Watch('pageSet')
    onPageSetChange(newVal: boardModule.state.PageSet) {
        this.itemsPerPage = this.boardConfig.itemsPerPage == null ? this.itemsPerPage : this.boardConfig.itemsPerPage;

        var searchSet = <article.SearchSet>{
            userCode: this.user.id,
            boardId: newVal.boardId,
            page: newVal.currentPage,
            itemsPerPage: this.itemsPerPage,
            searchField: newVal.searchField,
            searchFieldValue: newVal.searchText,
            prefaceId1: newVal.preface1,
            prefaceId2: newVal.preface2,
            sortField: this.currentField,
            sortOrder: this.sortOrder,
            includeContent: false,
            useNotice: this.boardConfig.usePrimaryNotice,
            useExpires: this.boardConfig.useExpire
        }
        this.fetchArticle(searchSet).then(res => {
            this.searchItems = res.totalCount;
            this.$emit('articlesChanged', this.itemsPerPage, res.totalCount, newVal.currentPage, res.data.length);
        })

        this.currentPageNum = newVal.currentPage
    }   

    created() {
        if (this.boardConfig != null) {
            this.onBoardChanged(this.boardConfig);
        }
    }   

    getlistFieldName(field: board.ListField) {
        return getLangProperty(field, 'displayName');        
    }

    getArticleFieldValue(article: article.Article, field: board.BoardField) {        
        var returnValue = null;
        if (field.fieldValueName != null) {
            if(field.fieldId == ids().ID_WRITERFIELD) {               
                returnValue = this.boardConfig.useNameless ? 
                        this.$t('W_BB_Anonymous') : 
                        // getLangProperty(article, "createdUserName");
                        this.getNameLang(article)
            } else if(field.fieldId == ids().ID_PREFACE) {
                returnValue = getLangProperty(article, field.fieldValueName);
            } else if(field.fieldValueName === 'upperPrefaceText') {
                returnValue = this.upperPrefaceText(article);
            } else {
                returnValue = getProperty(article, field.fieldValueName);
            }
        }
        else {
            var FieldValue = article.fieldValues.find(f => f.fieldId == field.fieldId);
            returnValue = FieldValue?.value;
        }

        if (returnValue == null) {
            return returnValue = "";
        } else if (field.fieldType === 'DATETIME' || field.fieldType === 'DATE') {
            var displayDate = IEDate(returnValue.toString());
            return uGetDateFormat('yyyy-MM-DD', displayDate);
        } else if (field.fieldType === 'SELECT') {
            var jObject = JSON.parse(returnValue.toString());
            return jObject.TEXT;
        } else {
            return returnValue;
        }
    }

    upperPrefaceText(item: article.Article): string {
        if(this.boardConfig != null) {
            var found1 = this.boardConfig.prefaces.find(p => p.prefaceId === item.prefaceId);

            if(found1) {
                var found2 = this.boardConfig.prefaces.find(p => p.prefaceId === found1?.parentPrefaceId)

                if(found2) {
                    return found2.prefaceText;
                }
            }
        }

        return '';
    }

    onClickSort(field: number) {
        this.fetchPageSet(<boardModule.state.PageSet>{
            boardId: this.boardConfig.boardId,
            currentPage: 1,
            searchField: 0,
            searchText: null,
            preface1: this.pageSet.preface1,
            preface2: this.pageSet.preface2,
            useNotice: this.pageSet.useNotice
        });

        this.currentField = field;
        this.sortOrder == false ? this.sortOrder = true : this.sortOrder = false;
    }

    getFieldAlign(field: board.ListField) {
        if (field.textAlign == null) {
            return { 'align-center': true, 'show-overflow': this.isWriter(field) }
        } else {
            var key = `align-${field.textAlign}`;
            var classObject = <common.DynamicClass>{};
            classObject[key] = true;
            classObject['show-overflow'] = this.isWriter(field);
            return classObject;
        }
    }

    getHeaderWidth(field: board.ListField) {
        if (field.fieldId === ids().ID_TITLEFIELD) {
            return null
        } else {
            return `${field.width}px`
        }
    }

    getPreface(article: article.Article) {
        return article.prefaceText == null ? "" : `[${article.prefaceText}]`;
    }

    isTitle(field: board.ListField) {
        if (field.fieldId == ids().ID_TITLEFIELD) {
            return true;
        } else {
            return false;
        }
    }

    isWriter(field: board.ListField) {
        if (field.fieldId == ids().ID_WRITERFIELD) {
            return true;
        } else {
            return false;
        }
    }

    setTitleCss(field: board.ListField) {
        if (field.fieldId == 1) {
            return { 'board-list': true }
        }
        else {
            return null;
        }
    }

    getTitlePadding(article: article.Article) {
        if (article.replyLevel > 0) {
            var padding = 20 * article.replyLevel
            return `${padding}px`;
        } else {
            return "0px";
        }
    }

    getNoticeCss(article: article.Article) {
        
        var rtCss = 'ft-600'
        if(article.isNotice == true && this.boardConfig.usePrimaryNotice){
            if(article.userView)
                rtCss= 'ft-red'
            else rtCss += ' ft-red'
        }
        else if(article.userView){
            rtCss = 'ft-gray'
        }
        return rtCss;   
        
        // if (article.isNotice == true && this.boardConfig.usePrimaryNotice) {
        //     return true
        // } else {
        //     return false
        // }
    }

    getStatusClass(article: article.Article): Object {
        var startTime = article.noticeStartDate == null ? 0 : IEDate(article.noticeStartDate).getTime();
        var endTime = article.noticeEndDate == null ? Number.MAX_SAFE_INTEGER : IEDate(article.noticeEndDate).getTime();
        var current = new Date();
        current.setHours(0, 0, 0, 0);

        var currentTime = current.getTime();
        
        if(startTime <= currentTime && currentTime <= endTime) {
            return { 'badge-green': true };
        } else {
            return { 'badge-lightgray': true };
        }
    }

    getStatusText(article: article.Article): string {
        var startTime = article.noticeStartDate == null ? 0 : IEDate(article.noticeStartDate).getTime();
        var endTime = article.noticeEndDate == null ? Number.MAX_SAFE_INTEGER : IEDate(article.noticeEndDate).getTime();
        var current = new Date();
        current.setHours(0, 0, 0, 0);

        var currentTime = current.getTime();
        
        if(startTime <= currentTime && currentTime <= endTime) {
            return this.$t('진행중').toString();
        } else if(currentTime < startTime) {
            return this.$t('준비중').toString();
        } else {
            return this.$t('종료').toString();
        }
    }

    showPoststatus(field: board.ListField) {
        return field.fieldType === 'POSTSTATUS'
    }

    showreplyLevel(article: article.Article) {
        if (article.parentArticle != null) {
            return true
        } else {
            return false
        }
    }

    showNotice(field: board.ListField, article: article.Article) {
        if (field.fieldValueName == 'articleSeq') {
            if (article.isNotice == true && this.boardConfig.usePrimaryNotice) {
                return true
            } else {
                return false
            }
        } else {
            return false;
        }
    }

    showNewIcon(article: article.Article) {
        var oneDay = 1000 * 60 * 60 * 24;
        var createdDate = new Date(article.createdDate.toString());
        var today = new Date();

        var timeSpan = today.getTime() - createdDate.getTime();


        if (timeSpan <= oneDay) {
            return true;
        } else {
            return false;
        }
    }

    showCommentCount(article: article.Article) {
        if (this.boardConfig.useComment && article.commentCount > 0) {
            return true;
        } else {
            return false;
        }
    }

    checkArticleExpired(expiredDate: Date | null, createduser: string) {
        if(expiredDate == null || createduser == this.user.personCode) {
            return false;
        } else {
            var tDate = new Date();
            var today = uGetDateFormat('yyyy-MM-DD', tDate);

            var eDate = new Date(expiredDate);
            var articlceDate = uGetDateFormat('yyyy-MM-DD', eDate);
            return articlceDate < today
        }
    }

    goDetailPage(article: article.Article) {   
        if(this.checkArticleExpired(article.expiredDate, article.createdUser)) {
            (window as any).alertAsync(this.$t("M_BB_ExpiredPost").toString());
            return;
        }

        var menuId = this.$route.query.MENUID ? this.$route.query.MENUID.toString() : '';

        if(this.previewMode) {
            this.$emit('open', { boardId: this.boardConfig.boardId, articleId: article.articleId, parentArticleId: article.parentArticle });
        } else {                         
            open(!this.isTeams && ( this.boardConfig.usePopup || this.adminMode ) ? 
                OpenType.PopupRead : OpenType.ModalRead,
                {
                    boardId: this.boardConfig.boardId,
                    articleId: article.articleId,
                    baseUrl: this.popupBaseUrl,
                    query: {
                        MENUID: menuId
                    }
                }); 
        }              
    }

    convertDateFormat(val:Date){
        return uGetDateFormat('yyyy-MM-DD', new Date(val));
    }

    /**
     * 사용자이름 중문 컬럼 추가
     */ 
    getNameLang(article:article.Article){
        let rtStr = ''
        if(this.current.userConfig.language=='en-US' && article.createdUserName_En && article.createdUserName_En!='')
            rtStr = article.createdUserName_En
        else if(this.current.userConfig.language=='zh-CN' && article.createdUserName_Ch && article.createdUserName_Ch!='')
            rtStr = article.createdUserName_Ch
        else rtStr = article.createdUserName
        return rtStr
    }

}